import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ForkKnife, ArrowsClockwise, Trash, Storefront } from "@phosphor-icons/react";
import { AddressInput } from "@/components/AddressInput";

function Dollar({ label, hint, value, onSave, testid }) {
  return (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-muted-foreground">$</span>
        <input data-testid={testid} type="number" min="0" step="0.25" defaultValue={((value ?? 0) / 100).toString()}
          onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== value) onSave(c); }}
          className="w-28 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Restaurants({ token }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", cuisine: "", owner_email: "", image: "" });
  const [loc, setLoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["admin-restaurants"], queryFn: async () => (await api.get("/eat/admin/restaurants")).data.restaurants });

  const create = async () => {
    if (!f.name || !loc) { toast.error("Add a name and pick an address"); return; }
    setBusy(true);
    try {
      await api.post("/eat/admin/restaurants", { ...f, address: loc.address, lat: loc.lat, lng: loc.lng });
      toast.success("Restaurant created");
      setF({ name: "", cuisine: "", owner_email: "", image: "" }); setLoc(null);
      qc.invalidateQueries({ queryKey: ["admin-restaurants"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/eat/admin/restaurants/${id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-restaurants"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="admin-restaurants">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><Storefront size={20} weight="duotone" /></div>
        <div><h2 className="font-display text-lg font-semibold">Restaurants</h2><p className="text-sm text-muted-foreground">Create restaurants and assign an owner (they manage the menu). Vendors can also self-register.</p></div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <input data-testid="rest-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Restaurant name"
          className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <input data-testid="rest-cuisine" value={f.cuisine} onChange={(e) => setF({ ...f, cuisine: e.target.value })} placeholder="Cuisine (e.g. Grill)"
          className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <input data-testid="rest-owner" value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} placeholder="Owner email (optional)"
          className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <input data-testid="rest-image" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="Image URL (optional)"
          className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <div className="sm:col-span-2"><AddressInput label="Address" token={token} value={loc} onSelect={setLoc} testid="rest-address" /></div>
      </div>
      <button data-testid="rest-create" onClick={create} disabled={busy}
        className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? "Creating…" : "Create restaurant"}</button>

      <div className="mt-5 space-y-2">
        {(data || []).length === 0 && <p className="text-sm text-muted-foreground">No restaurants yet.</p>}
        {(data || []).map((r) => (
          <div key={r.id} data-testid={`rest-row-${r.id}`} className="flex items-center justify-between rounded-xl bg-background px-3 py-2 text-sm">
            <div><span className="font-medium">{r.name}</span> <span className="text-xs text-muted-foreground">· {r.cuisine || "—"} · {r.menu_count} items {r.active ? "" : "· inactive"}</span></div>
            <button data-testid={`rest-del-${r.id}`} onClick={() => del(r.id)} className="text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function AdminEat() {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["platform-settings"], queryFn: async () => (await api.get("/settings/platform")).data });
  const { data: cfg } = useQuery({ queryKey: ["eat-config"], queryFn: async () => (await api.get("/eat/config")).data });
  const s = data?.settings;

  const save = async (patch) => {
    setSaving(true);
    try { await api.put("/settings/platform", patch); toast.success("Saved"); qc.invalidateQueries({ queryKey: ["platform-settings"] }); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  if (!s) return <div className="text-muted-foreground">Loading…</div>;
  const pct = s.eat_commission_pct ?? 0;

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-eat">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">iYeneh Eat</h1>
        <p className="mt-1 text-sm text-muted-foreground">Set delivery fees, your commission on food orders, and manage restaurants.</p>
      </div>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><ForkKnife size={20} weight="duotone" /></div>
          <div><h2 className="font-display text-lg font-semibold">Availability</h2><p className="text-sm text-muted-foreground">Turn the iYeneh Eat module on or off.</p></div>
        </div>
        <label className="mt-4 flex items-start gap-3 rounded-xl border border-white/10 p-3">
          <input type="checkbox" data-testid="eat-enabled" checked={!!s.eat_enabled} onChange={(e) => save({ eat_enabled: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">Food ordering enabled</span>
        </label>
      </section>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Delivery fee</h2>
        <p className="mt-1 text-sm text-muted-foreground">Fee = base + (per-km × distance from restaurant to the customer), never below the minimum. The courier earns this fee.</p>
        <div className="mt-4 grid gap-5 sm:grid-cols-3">
          <Dollar label="Base fee" testid="eat-base" value={s.eat_base_delivery_cents} onSave={(v) => save({ eat_base_delivery_cents: v })} />
          <Dollar label="Per kilometre" testid="eat-perkm" value={s.eat_per_km_cents} onSave={(v) => save({ eat_per_km_cents: v })} />
          <Dollar label="Minimum fee" testid="eat-min" value={s.eat_min_delivery_cents} onSave={(v) => save({ eat_min_delivery_cents: v })} />
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Your commission</h2>
        <p className="mt-1 text-sm text-muted-foreground">Percentage of the food subtotal the platform keeps; the restaurant gets the rest.</p>
        <div className="mt-4 flex items-center gap-2">
          <input data-testid="eat-commission" type="number" min="0" max="100" step="1" defaultValue={pct.toString()}
            onBlur={(e) => { const v = Math.max(0, Math.min(100, Number(e.target.value) || 0)); if (v !== pct) save({ eat_commission_pct: v }); }}
            className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <span className="text-sm text-muted-foreground">% of food subtotal</span>
        </div>
      </section>

      <Restaurants token={cfg?.mapbox_token} />

      {saving && <p className="flex items-center gap-2 text-sm text-muted-foreground"><ArrowsClockwise size={14} className="animate-spin" /> Saving…</p>}
    </div>
  );
}
